import type { LiveContextStateDto } from '@/state/queries/player/use-get-live-context-state';
import { isLookAheadByPlayerKey, isPreviewByPlayerKey } from '@/helpers/resolve-output-preview-layers';

const SEQUENCED_COMPONENT_IDS = new Set(['imageSequence', 'presentationSlides']);

/** Normalize bare media ids from live payloads to staged item ids (`media:…`). */
export function normalizeMediaItemId(id: string | undefined): string | undefined {
  if (!id) {
    return undefined;
  }
  if (id.startsWith('media:')) {
    return id;
  }
  if (!id.includes(':')) {
    return `media:${id}`;
  }
  return undefined;
}

function collectFromLayers(layers: { componentId?: string; itemId?: string }[] | undefined, out: Set<string>) {
  for (const layer of layers ?? []) {
    if (layer.itemId && SEQUENCED_COMPONENT_IDS.has(layer.componentId ?? '')) {
      out.add(layer.itemId);
    }
  }
}

/**
 * Item ids for **on-air** image-sequence / presentation-slide layers only.
 * Excludes `_lookahead:*` / `_preview:*` — unscoped sequence WS must not stamp Next/preview decks.
 */
export function resolveLiveSequenceItemIds(liveContextState?: LiveContextStateDto): string[] {
  const ids = new Set<string>();
  collectFromLayers(liveContextState?.playState?.layers, ids);

  const byPlayer = liveContextState?.playState?.byPlayer;
  if (byPlayer) {
    for (const [playerKey, entry] of Object.entries(byPlayer)) {
      if (isLookAheadByPlayerKey(playerKey) || isPreviewByPlayerKey(playerKey)) {
        continue;
      }
      collectFromLayers(entry.layers, ids);
    }
  }

  return [...ids];
}

/** True when this preview is the cue's on-air media, not the Next look-ahead of the same store. */
export function isOnAirCueSequenceItem(itemId: string | undefined, currentStepItemId: string | undefined): boolean {
  const layerItem = normalizeMediaItemId(itemId);
  const currentItem = normalizeMediaItemId(currentStepItemId);
  return Boolean(layerItem && currentItem && layerItem === currentItem);
}

export function resolveLayerSequenceItemId(layer: {
  itemId?: string;
  data?: Record<string, string>;
}): string | undefined {
  const fromItemId = normalizeMediaItemId(layer.itemId);
  if (fromItemId) {
    return fromItemId;
  }
  if (layer.itemId) {
    return layer.itemId;
  }
  return normalizeMediaItemId(layer.data?.mediaId);
}
